import { useEffect, useRef } from 'react'

import {
  announceEngagement,
  announceFormState,
  announceDonationCompleted,
} from './abandonedCartClient'

// Donate-widget-side glue for the abandoned-cart feature. One hook
// that owns:
//
//   - Engagement-level tracking (presets clicked → L1, typed/pasted
//     → L2) via a passive `input` listener on document.
//   - Announcement of every level transition to the manager (local
//     in mode 1 standalone, channel in modes 2+3) so the manager
//     can gate its triggers on engagement.
//   - Reset + donation-completed announcement when the donation
//     transitions to truthy, so a "Donate Again" + form reset
//     starts a fresh session at L0.
//
// Levels are monotonically non-decreasing within a session. After
// completion, the input listener no-ops (post-donation typing on
// the thank-you panel's Comment doesn't count).
//
// Detection is passive: one `input` listener on document. `input`
// fires for radio/checkbox/select changes as well as text typing,
// so one listener covers both levels. Listening on document rather
// than a widget-root ref so portaled modals (v7's Comment + Donor
// Feed) still route their input events to us. In v6 nothing else
// interactive lives on the widget's page/iframe, so document-level
// listening captures the same events a ref listener would have.
//
// Level state lives on a ref rather than useState on purpose: a
// React state update from this document-level handler would
// re-render mid-event, and a <select>'s native `input` event fires
// before its `change` event (which is what React's onChange binds
// to). A re-render between the two writes select.value back from
// the stale partnerItem state, the change event then reads that
// reverted DOM value, and the donor's pick is silently lost. The
// announce-on-transition path calls announceEngagement directly
// instead, leaving form-component state untouched.
//
// Out of reach by design: Stripe Elements live in cross-origin
// iframes, so card-field typing never fires events on our document.
// Donors who only ever typed into the card iframe stay at L1 (from
// amount/frequency picks) or L0 — acceptable for v1.
//
// Form-state relay piggybacks on the same input listener: inputs
// tagged with `data-plg-donor-field="email|partnerItem"` route their
// values into announceFormState so the manager can pre-fill the
// corner dialog with what the donor was filling in.
//
// See project_abandoned_cart memory for the broader plan.

const FIELD_PUSHERS = {
  email: el => announceFormState({ email: el.value }),
  partnerItem: el => announceFormState({ partnerItemExternalId: el.value }),
}

const levelForEventTarget = target => {
  if (!target) return 0
  const { tagName, type } = target
  if (tagName === 'INPUT' && (type === 'radio' || type === 'checkbox')) return 1
  if (tagName === 'SELECT') return 1
  if (tagName === 'INPUT' || tagName === 'TEXTAREA') return 2
  return 0
}

export default function useAbandonedCart({
  completed,
  enabled,
  partnerKey,
  defaultExternalId,
  locale,
} = {}) {
  const levelRef = useRef(0)
  const completedRef = useRef(completed)

  // Mirror completed into a ref so the long-lived input listener
  // reads the latest value without re-attaching on every toggle.
  useEffect(() => { completedRef.current = completed }, [ completed ])

  // On donation completion: drop the level back to 0 and announce
  // so the manager flips its fired flag (suppresses any further
  // triggers for this page lifetime).
  useEffect(() => {
    if (!completed) return
    levelRef.current = 0
    announceDonationCompleted()
  }, [ completed ])

  // Build the abandoned-cart payload — null when the per-PWC toggle
  // is off (feature disabled for this widget). Recomputed every
  // render; both useEffects below close over it and depend on its
  // primitive parts to re-run when any of them change.
  //
  // location.origin is the donate widget's own origin (pledge.to in
  // all three modes — the donate widget iframe is served from
  // pledge.to even on a partner page). The manager runs on the
  // partner page in modes 2+3, so relative paths there would
  // resolve against the partner's origin; pin to ours.
  //
  // background matches Bootstrap 5's --bs-body-bg under
  // data-bs-theme="dark", which the abandoned-cart iframe's layout
  // resolves to. The corner-dialog body shows through any
  // transparent area of the still-loading iframe (and around it on
  // short content), so pinning it to the eventual bg avoids a
  // white flash on slow connections.
  // If the donate widget's outer iframe URL carried a
  // ?style_brand_color override (FLP-rendered context, partner-page
  // embed-script with the param, etc.), forward it to the corner-
  // dialog iframe URL so AbandonedCartController's pwc_overrides
  // picks it up and the dialog's chrome matches the donate widget's
  // chrome. Pulled from location.search at hook-call time rather
  // than threaded as a prop — the hook already runs in the iframe
  // whose URL has the param.
  const styleBrandColor = new URLSearchParams(location.search)
    .get('style_brand_color')

  const abandonedCart = enabled ? {
    origin: location.origin,
    partnerKey,
    defaultExternalId,
    ariaLabel: 'Finish your donation',
    locale,
    background: '#212529',
    styleBrandColor,
  } : null

  // Announce on mount + on every opts change. The manager's gating
  // treats anything < 1 as "no engagement," so the initial signal
  // is a harmless restate that also lets the manager learn the
  // abandoned-cart URL parts + locale before any engagement
  // transitions happen.
  useEffect(() => {
    announceEngagement({ level: levelRef.current, abandonedCart })
  }, [ enabled, partnerKey, defaultExternalId, locale ])

  // Pure-side-effect listener: no React state mutations, so the
  // re-render-mid-event problem on selects (see header comment)
  // doesn't apply. Engagement transitions announce directly; form
  // fields with data-plg-donor-field push into the manager via
  // announceFormState.
  useEffect(() => {
    const onInput = e => {
      if (completedRef.current) return
      const next = levelForEventTarget(e.target)
      if (next > levelRef.current) {
        levelRef.current = next
        announceEngagement({ level: next, abandonedCart })
      }
      FIELD_PUSHERS[e.target.dataset?.plgDonorField]?.(e.target)
    }

    document.addEventListener('input', onInput)
    return () => document.removeEventListener('input', onInput)
  }, [ enabled, partnerKey, defaultExternalId, locale ])
}
